import React, { useState, useCallback, useMemo, type SyntheticEvent } from 'react';
import Button from '@atlaskit/button';
import ModalDialog, {
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
	ModalTransition,
	type KeyboardOrMouseEvent,
} from '@atlaskit/modal-dialog';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey, useProjectKey } from '@atlassian/jira-issue-context-service';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectPermissions } from '@atlassian/jira-project-permissions-service';
import { useAccountId } from '@atlassian/jira-tenant-context-controller';
import type { AttachmentId } from '../../common/types';
import { useIssueAttachments } from '../../services/attachments-service';
// eslint-disable-next-line jira/import/no-parent-imports
import { type AttachmentView, filmStripView } from '../types';
import i18n from './messages';
import type { InjectedProps } from './types';
import { getCanDeleteAttachmentMap } from './utils';

// visible for testing
export const useDeletingAttachmentId = () => useState<AttachmentId>();

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default <Config,>(
		WrappedComponent: Flow.AbstractComponent<Config & InjectedProps>,
		attachmentView: AttachmentView = filmStripView,
	): Flow.AbstractComponent<Config> =>
	// @ts-expect-error - TS2322 - Type '(props: Config) => JSX.Element' is not assignable to type 'AbstractComponent<Config, any>'.
	(props: Config) => {
		const issueKey = useIssueKey();
		const [
			{
				value: { attachments },
			},
			{ deleteAttachment },
		] = useIssueAttachments(issueKey);
		const [deletingAttachmentId, setDeletingAttachmentId] = useDeletingAttachmentId();
		const { formatMessage } = useIntl();
		const projectKey = useProjectKey();
		const [permissions] = useProjectPermissions(projectKey);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const accountIdLoggedInUser = useAccountId();

		const onDeleteAttachment = useCallback(
			(event: SyntheticEvent<HTMLElement>) => {
				if (deletingAttachmentId !== undefined) {
					event.stopPropagation();
					const analyticsEvent = createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'button',
					});
					fireUIAnalytics(analyticsEvent, 'deleteAttachmentConfirm', {
						view: attachmentView,
					});
					deleteAttachment(issueKey, deletingAttachmentId);
					// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
					setDeletingAttachmentId();
				}
			},
			[
				createAnalyticsEvent,
				deleteAttachment,
				deletingAttachmentId,
				issueKey,
				setDeletingAttachmentId,
			],
		);

		const onDismissModal = useCallback(
			(event: KeyboardOrMouseEvent) => {
				event.stopPropagation();
				// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
				setDeletingAttachmentId();
			},
			[setDeletingAttachmentId],
		);

		const canDeleteAttachmentMap = useMemo(
			() =>
				getCanDeleteAttachmentMap({
					attachments,
					canDeleteAllAttachments: permissions.canDeleteAllAttachments,
					canDeleteOwnAttachments: permissions.canDeleteOwnAttachments,
					accountIdLoggedInUser,
				}),
			[
				accountIdLoggedInUser,
				attachments,
				permissions.canDeleteAllAttachments,
				permissions.canDeleteOwnAttachments,
			],
		);

		return (
			<>
				{/* @ts-expect-error - TS2322 - Type 'Config & { onDeleteAttachment: Dispatch<SetStateAction<string | undefined>>; canDeleteAttachmentMap: Partial<Record<string, boolean>>; }' is not assignable to type 'IntrinsicAttributes & PropsWithoutRef<Config & InjectedProps> & RefAttributes<any>'. */}
				<WrappedComponent
					{...props}
					onDeleteAttachment={setDeletingAttachmentId}
					canDeleteAttachmentMap={canDeleteAttachmentMap}
				/>
				<ModalTransition>
					{deletingAttachmentId !== undefined && (
						<ShortcutScope>
							<ModalDialog onClose={onDismissModal} width="small">
								<ModalHeader>
									<ModalTitle appearance="danger">
										{formatMessage(i18n.confirmationHeader)}
									</ModalTitle>
								</ModalHeader>
								<ModalBody>{formatMessage(i18n.confirmationContent)}</ModalBody>
								<ModalFooter>
									<Button
										appearance="danger"
										onClick={onDeleteAttachment}
										testId="issue-attachments-base.ui.with-issue-attachments-delete.confirm-button"
										interactionName="auto-generated.issue-attachments-base.ui.with-issue-attachments-delete.confirm-button.button"
									>
										{formatMessage(i18n.confirmationSubmit)}
									</Button>
									<Button
										appearance="subtle"
										onClick={onDismissModal}
										testId="issue-attachments-base.ui.with-issue-attachments-delete.cancel-button"
										interactionName="auto-generated.issue-attachments-base.ui.with-issue-attachments-delete.cancel-button.button1"
									>
										{formatMessage(i18n.confirmationCancel)}
									</Button>
								</ModalFooter>
							</ModalDialog>
						</ShortcutScope>
					)}
				</ModalTransition>
			</>
		);
	};
